import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as date from '@owl-nest/date'
import * as api from '@owl-nest/api-client/latest'
import { t, tc, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'

export function ExtraTimeBlock({ project }: { project: api.Project }): React.ReactElement | null {
  const baseBackofficePath = `/projects/${project.id}/backoffice`

  const endDate = project.date_end
  const extraTimeEndDate = project.date_end_extra_time
  const numberMinutesLeft = endDate ? date.differenceInMinutes(date.addDays(new Date(endDate), 7), new Date()) : 0
  const numberDayToEnd = endDate ? date.differenceInDays(new Date(endDate), new Date()) : 0

  const extraTimeStatus = models.project.extratime.getExtraTimeStatus({
    numberMinutesLeft,
    numberDayToEnd,
    extraTimeActivated: models.project.hasExtraTime(project),
    project,
  })
  const userConfig = env.useUserEnv()

  if (models.project.extratime.isDisabled(extraTimeStatus) || models.project.extratime.isEnded(extraTimeStatus)) {
    return <></>
  }

  const isextraTimeOn = extraTimeStatus === models.project.extratime.ExtraTimeStatus.EXTRA_TIME_IS_ON
  const isExtraTimeSaved = extraTimeStatus === models.project.extratime.ExtraTimeStatus.ONLINE_WITH_EXTRA_TIME_SAVED
  const locale = userConfig.lang

  return (
    <Wrapper>
      {isExtraTimeSaved ? (
        <SmallTitle>
          <plume.glyphs.fill.CheckCircle size={17} /> {t('Extension activated')}
        </SmallTitle>
      ) : isextraTimeOn ? (
        <SmallTitle>
          <plume.glyphs.fill.CheckCircle size={17} /> {t('Extension in progress')}
        </SmallTitle>
      ) : (
        <Title>
          <plume.glyphs.twoToned.StopwatchPlus /> {t('Extension')}
        </Title>
      )}

      {(isExtraTimeSaved || isextraTimeOn) && extraTimeEndDate ? (
        <TextInfo>
          {tc('Until %(date)s at %(hours)s. [Link: Edit]', {
            date: date.format(new Date(extraTimeEndDate), 'dd MMMM yyyy'),
            hours:
              locale === 'fr'
                ? date.format(new Date(extraTimeEndDate), "H'h'mm")
                : date.format(new Date(extraTimeEndDate), 'p'),

            Link: <plume.Link underlined href={`${baseBackofficePath}/offboarding`} />,
          })}
        </TextInfo>
      ) : (
        <>
          {models.project.isOnline(project) && !models.project.isFinished(project) ? (
            <>
              <Description>{t('Want to continue the journey? Extend your project 👇')}</Description>
              <plume.RoundButton forwardedAs={'a'} href={`${baseBackofficePath}/offboarding`}>
                {t('Activate')}
              </plume.RoundButton>
            </>
          ) : (
            numberMinutesLeft > 0 && (
              <>
                <Description>
                  {tc(`You only have [strong: %(nbDays)d %(days)s] left to set up your extensions.`, {
                    strong: <strong />,
                    nbDays:
                      numberMinutesLeft > 24 * 60
                        ? Math.floor(numberMinutesLeft / (24 * 60))
                        : numberMinutesLeft > 60
                          ? Math.floor(numberMinutesLeft / 60)
                          : numberMinutesLeft,
                    days:
                      numberMinutesLeft > 24 * 60
                        ? tn('day', 'days', Math.floor(numberMinutesLeft / (24 * 60)))
                        : numberMinutesLeft > 60
                          ? tn('hour', 'hours', Math.floor(numberMinutesLeft / 60))
                          : tn('minute', 'minutes', numberMinutesLeft),
                  })}
                </Description>
                <plume.RoundButton forwardedAs={'a'} href={`${baseBackofficePath}/offboarding`}>
                  {t('Activate')}
                </plume.RoundButton>
              </>
            )
          )}
        </>
      )}
    </Wrapper>
  )
}

const Title = styled(plume.styles.heading.XXXXS)`
  align-items: center;
  color: ${plume.COLORS.TEXT_PRIMARY};
  display: flex;

  ${plume.glyphs.twoToned.StopwatchPlus} {
    margin-right: 5px;
  }
`

const Description = styled(plume.styles.copy.S)`
  text-align: center;
`

const Wrapper = styled.div`
  align-items: center;
  border-radius: 8px;
  box-shadow: ${plume.EFFECTS.SHADOW_2};
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px auto;
  max-width: 172px;
  padding: 20px 12px;
  width: 196px;
`

const SmallTitle = styled(plume.styles.copy.S)`
  align-items: center;
  display: flex;
  font-weight: 600; // HACK: Irregular font manipulation. We'll be adding copy.S.semiBold, so this is just by anticipation.

  ${plume.glyphs.fill.CheckCircle} {
    color: ${plume.COLORS.SECONDARY_GREEN_500};
    margin-right: 5px;
  }
`

const TextInfo = styled(plume.styles.copy.S)`
  text-align: center;
`
